import React, { createContext, useContext, useState, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { TitleGenerationModal } from '../../components/Modal';
import { useNotification } from '../NotificationContext';
import { 
  generateTitlesFromVideo, 
  generateTitlesFromAudio, 
  generateTitlesFromText,
  generateTitlesFromMediaUrl
} from '../../services/titleGenerationApi';

// Define the context interface
interface TitleGenerationContextType {
  isTitleModalOpen: boolean;
  openTitleModal: (mediaType: 'video' | 'audio' | 'none', mediaUrl?: string, projectId?: string, existingTitleData?: any) => void;
  closeTitleModal: () => void;
  processTitleGeneration: (description?: string, projectId?: string) => Promise<string>;
  isProcessing: boolean;
}

// Create the context
const TitleGenerationContext = createContext<TitleGenerationContextType | undefined>(undefined);

// Create the provider component
interface TitleGenerationProviderProps {
  children: ReactNode;
}

export const TitleGenerationProvider: React.FC<TitleGenerationProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  
  const { showNotification } = useNotification();
  
  const [isTitleModalOpen, setIsTitleModalOpen] = useState(false);
  const [mediaType, setMediaType] = useState<'video' | 'audio' | 'none'>('none');
  const [mediaUrl, setMediaUrl] = useState<string | undefined>(undefined);
  const [isProcessing, setIsProcessing] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [showDashboard, setShowDashboard] = useState(false);
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [existingProjectId, setExistingProjectId] = useState<string | undefined>(undefined);
  const [existingTitleData, setExistingTitleData] = useState<any>(null);

  // Open the title generation modal
  const openTitleModal = (mediaType: 'video' | 'audio' | 'none', mediaUrl?: string, projectId?: string, titleData?: any) => {
    setMediaType(mediaType);
    setMediaUrl(mediaUrl);
    setExistingProjectId(projectId);
    setExistingTitleData(titleData);
    
    console.log('Opening title modal with:', { mediaType, mediaUrl, projectId, titleData });
    
    // Skip file extraction if it's an S3 URL - we'll pass it directly to the backend
    const isS3Url = mediaUrl && (
      mediaUrl.includes('amazonaws.com') || 
      mediaUrl.includes('s3.') || 
      mediaUrl.startsWith('https://s3-')
    );
    
    if (mediaUrl && !isS3Url) {
      // Only extract file if it's not an S3 URL
      extractFileFromMediaUrl(mediaUrl, mediaType);
    } else {
      // Clear any previous file
      setMediaFile(null);
    }
    
    setIsTitleModalOpen(true);
  };

  // Extract File object from media URL (only used for non-S3 URLs)
  const extractFileFromMediaUrl = async (url: string, type: 'video' | 'audio' | 'none') => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const filename = url.split('/').pop() || 'media-file';
      const mimeType = type === 'video' ? 'video/mp4' : 'audio/mp3';
      
      const file = new File([blob], filename, { type: mimeType });
      setMediaFile(file);
    } catch (error) {
      console.error('Error extracting file from URL:', error);
      setMediaFile(null);
    }
  };

  // Close the title generation modal
  const closeTitleModal = () => {
    setIsTitleModalOpen(false);
    setMediaFile(null);
  };

  // Process the title generation request
  const processTitleGeneration = async (description?: string, projectId?: string): Promise<string> => {
    // Use provided projectId or fallback to the one from modal opening
    const effectiveProjectId = projectId || existingProjectId;
    
    console.log('Processing title generation with:', { 
      mediaType, 
      mediaUrl, 
      description, 
      projectId: effectiveProjectId || 'None (will create new)' 
    });
    try {
      setIsProcessing(true);
      setShowDashboard(false);
      
      // Call the appropriate API based on the available media options
      let projectId: string;
      
      // Check if URL is an S3 URL (more reliable than downloading and re-uploading)
      const isS3Url = mediaUrl && (
        mediaUrl.includes('amazonaws.com') || 
        mediaUrl.includes('s3.') || 
        mediaUrl.startsWith('https://s3-')
      );
      
      if (isS3Url && mediaUrl) {
        // Use the S3 URL directly for better performance
        console.log(`Using S3 URL directly for ${mediaType} processing:`, mediaUrl);
        projectId = await generateTitlesFromMediaUrl(mediaUrl, mediaType, description, effectiveProjectId);
      } 
      // Fallback to local file upload if we have a file but not an S3 URL
      else if (mediaType === 'video' && mediaFile) {
        projectId = await generateTitlesFromVideo(mediaFile, description, effectiveProjectId);
      } else if (mediaType === 'audio' && mediaFile) {
        projectId = await generateTitlesFromAudio(mediaFile, description, effectiveProjectId);
      } else if (description) {
        // If no media at all, just use the text description
        projectId = await generateTitlesFromText(description, effectiveProjectId);
      } else {
        throw new Error('No media or description provided');
      }
      
      // Show dashboard with job ID
      //setJobId(projectId);
      setShowDashboard(true);
      
      return projectId;
    } catch (error) {
      console.error('Error processing title generation:', error);
      showNotification({
        title: 'Error',
        message: 'Failed to process title generation',
        type: 'error'
      });
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle completion callback from dashboard
  const handleCompletionCallback = (projectId: string) => {
    setShowDashboard(false);
    closeTitleModal();
    
    // Navigate to the title description page
    navigate(`/title-description/${projectId}`);
    
    showNotification({
      title: 'Success',
      message: 'Title generation completed successfully',
      type: 'success'
    });
  };

  return (
    <TitleGenerationContext.Provider
      value={{
        isTitleModalOpen,
        openTitleModal,
        closeTitleModal,
        processTitleGeneration,
        isProcessing
      }}
    >
      {children}
      <TitleGenerationModal
        isOpen={isTitleModalOpen}
        onClose={closeTitleModal}
        onProcessContent={(description) => processTitleGeneration(description, existingProjectId)}
        mediaType={mediaType}
        mediaUrl={mediaUrl}
        isProcessing={isProcessing}
        showDashboard={showDashboard}
        jobId={jobId}
        onCompletionCallback={handleCompletionCallback}
        projectId={existingProjectId}
        existingTitleData={existingTitleData}
      />
    </TitleGenerationContext.Provider>
  );
};

// Custom hook to use the title generation context
export const useTitleGeneration = (): TitleGenerationContextType => {
  const context = useContext(TitleGenerationContext);
  if (!context) {
    throw new Error('useTitleGeneration must be used within a TitleGenerationProvider');
  }
  return context;
};